import React from 'react';
import DrawTrackIcon from '../../../icons/DrawTrackIcon';
import DrawWaypointIcon from '../../../icons/DrawWaypointIcon';
import {WaypointDispatcher} from '../../../hooks/useWaypoints';
import {TrackDispatcher} from '../../../hooks/useTracks';
import {SPRING_1} from '../../../colors';
import SubscriptionTooltip from '../SubscriptionTooltip';
import fetchUser from '../../../utils/gaia/fetchUser';
import useSyncPromise from '../../../utils/react/useSyncPromise';
import {
    trackFixedLayerIds,
    waypointFixedLayerIds
} from '../../../utils/gaia/getSyncMapboxStyle/fixedLayerIds';

import {
    MapboxControlButton,
    MapboxControlGroup,
    createReactMapboxControl
} from '../../../utils/mapbox/Mapbox';

export interface DrawControlProps {
    interaction: MapInteraction;
    onInteractionChange: (interaction: MapInteraction) => void;
    onPopupChange: (popup: MapPopup | undefined) => void;
    waypointDispatcher: WaypointDispatcher;
    trackDispatcher: TrackDispatcher;
    map: mapboxgl.Map | undefined;
    overlays: MapOverlays;
    onOverlaysChange: SetState<MapOverlays>;
}

function DrawControl({
    interaction,
    onInteractionChange,
    onPopupChange,
    waypointDispatcher,
    trackDispatcher,
    overlays,
    onOverlaysChange,
    map,
}: DrawControlProps) {
    const [user] = useSyncPromise(fetchUser());

    return <MapboxControlGroup>
        <MapboxControlButton
            style={interaction.type === 'movingWaypoint' && !interaction.initialWaypoint ?
                {color: SPRING_1} :
                undefined
            }
            disabled={Boolean(
                !user ||
                !user.isAuthenticated ||
                interaction.type === 'movingWaypoint' ||
                interaction.type === 'editingRoute'
            )}
            onClick={() => {
                if (!map) return;
                const pointLocalId = waypointDispatcher({type: 'create', lngLat: map.getCenter()});
                onInteractionChange({
                    subtype: 'normal',
                    type: 'movingWaypoint',
                    pointLocalId,
                    mapboxLayerId: waypointFixedLayerIds.localUser.pin,
                });
                onOverlaysChange({...overlays, showWaypoints: true});
            }}
        >
            <SubscriptionTooltip
                enterDelay={750}
                placement='right'
                title='Create Waypoint'
                subscription='user'
            >
                <div><DrawWaypointIcon style={{
                    position: 'relative',
                    top: 2,
                    left: 2,
                    width: 24,
                    height: 24
                }} /></div>
            </SubscriptionTooltip>
        </MapboxControlButton>


        <MapboxControlButton
            style={interaction.type === 'editingRoute' ?
                {color: SPRING_1} :
                undefined
            }
            disabled={Boolean(
                !user ||
                !user.isAuthenticated ||
                interaction.type === 'movingWaypoint' ||
                interaction.type === 'editingRoute'
            )}
            onClick={() => {
                if (!map) return;
                const trackLocalId = trackDispatcher({
                    type: 'create',
                    lngLat: map.getCenter(),
                });
                onInteractionChange({
                    subtype: 'appendingPoint',
                    type: 'editingRoute',
                    cursorLngLat: undefined,
                    trackLocalId,
                    idle: false,
                    mapboxLayerId: trackFixedLayerIds.localUser.normal
                });
                onOverlaysChange({...overlays, showRoutes: true});
                onPopupChange(undefined);
            }}
        >
            <SubscriptionTooltip
                placement='right'
                title='Create Route'
                subscription='user'
                enterDelay={750}
            >
                <div><DrawTrackIcon style={{
                    position: 'relative',
                    top: -1,
                    height: 24,
                    width: 24
                }} /></div>
            </SubscriptionTooltip>
        </MapboxControlButton>
    </MapboxControlGroup>;
}

export default createReactMapboxControl(DrawControl);
